import { useEffect, useState, useContext } from "react"
import Header from "../../../layouts/Header"
import BackIcon from "../../../components/BackIcon"
import { useNavigate, useLocation } from "react-router-dom"
import { Box, Paper, TextField, Tooltip, IconButton, Grid, MenuItem, Typography } from "@mui/material"
import { createTheme, ThemeProvider } from "@mui/material/styles"
import CustomBackdrop from "../../../components/CustomBackdrop"
import { useMutation, api, showWarning } from "../../../api"
import { GlobalContext } from "../../../contexts/GlobalContext"
import getIconComponent from "../../utils/getIconComponent"
import Save from "@mui/icons-material/Save"

const theme = createTheme({
  palette: {
    primary: { main: "#196C87" },
    secondary: { main: "#2E7D32" },
  },
})

const StyledRoot = {
  width: "100%",
  maxWidth: "1200px",
  margin: "0 auto",
  padding: "20px",
  backgroundColor: "#f5f7fa",
  borderRadius: "12px",
  boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
}

const EditarInventariosINV = () => {
  const navigate = useNavigate()
  const { state } = useLocation() // Recibimos la fila seleccionada desde la grilla[cite: 18]
  const { selectedMenuInfo } = useContext(GlobalContext)

  // Mutación para actualizar el inventario[cite: 18]
  const {
    mutateAsync: SaveEdicionInventario,
    isPending: isSaving,
    isError: isErrorSaving,
  } = useMutation({
    queryKey: ["isEditingInventarioINV"],
    fn: async (data) => {
      const response = await api.post("/InventariosINV/updateInventariosINV", data)
      return response.data
    },
    showError: "modal",
    showSuccess: "toast",
    onSuccess: () => navigate(-1),
  })

  // Guardamos el código original para enviarlo como 'Old' al backend[cite: 18]
  const invcodigoViejo = state?.invcodigo ?? ""

  // Estado del formulario para los datos editables
  const [formData, setFormData] = useState({
    invcodigo: "",
    invdescri: "",
    invstatus: "A",
  })

  useEffect(() => {
    // Precargar los datos al entrar a la pantalla[cite: 18]
    if (state) {
      setFormData({
        invcodigo: state.invcodigo || "",
        invdescri: state.invdescri || "",
        invstatus: state.invstatus || "A",
      })
    } else {
      navigate(-1) // Retorno de seguridad si no hay datos en el state
    }
  }, [state, navigate])

  // Manejar cambios con normalización y topes de caracteres[cite: 18]
  const handleInputChange = (field, value) => {
    let finalValue = value.toUpperCase()

    if (field === "invcodigo") finalValue = finalValue.slice(0, 2)
    if (field === "invdescri") finalValue = finalValue.slice(0, 30)

    setFormData((prev) => ({
      ...prev,
      [field]: finalValue,
    }))
  }

  const handleSubmit = async (e) => {
    if (e) e.preventDefault()

    // Validaciones de campos obligatorios
    if (!formData.invcodigo.trim()) return showWarning("El Código de Inventario es obligatorio")
    if (!formData.invdescri.trim()) return showWarning("La Descripción de Inventario es obligatoria")

    // Preparar el payload con la lógica de cambio de llave[cite: 18]
    const payload = {
      invcodigoOld: invcodigoViejo,
      invcodigoNew: formData.invcodigo,
      invdescri: formData.invdescri,
      invstatus: formData.invstatus,
    }

    try {
      await SaveEdicionInventario(payload)
    } catch (error) {
      console.error("Error al editar el inventario:", error)
    }
  }

  // Buscar acción de actualización en la barra de menú[cite: 18]
  const actualizarAction = selectedMenuInfo?.data?.barraAcciones?.find(
    (action) =>
      action?.acccaption === "ACTUALIZAR" || action?.acccaption === "GUARDAR" || action?.acccaption === "EJECUTAR",
  )

  const toolbarActions = actualizarAction
    ? [
        {
          label: actualizarAction.acccaption,
          key: actualizarAction.acccaption,
          icon: getIconComponent(actualizarAction.accnameicono, actualizarAction.acctipoico),
        },
      ]
    : [{ label: "Actualizar", key: "ACTUALIZAR", icon: <Save /> }]

  return (
    <ThemeProvider theme={theme}>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        <BackIcon />

        <Box sx={{ mb: 2 }}>
          {toolbarActions.map((action) => (
            <Tooltip title={action.label} key={action.key}>
              <IconButton
                onClick={handleSubmit}
                disabled={isSaving}
                sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1 }}
              >
                {action.icon}
              </IconButton>
            </Tooltip>
          ))}
        </Box>

        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            margin: "0 30px 30px 30px",
            fontSize: "25px",
          }}
        >
          <b>Editar Inventario: {invcodigoViejo}</b>
        </div>

        <CustomBackdrop isLoading={isSaving} />

        <Box sx={StyledRoot}>
          <Paper
            elevation={3}
            sx={{ p: 4, borderRadius: 3, background: "white" }}
            component="form"
            onSubmit={handleSubmit}
          >
            <Typography variant="h6" color="primary" gutterBottom sx={{ mb: 3 }}>
              Datos del Inventario
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  label="Código de Inventario *"
                  value={formData.invcodigo}
                  onChange={(e) => handleInputChange("invcodigo", e.target.value)}
                  error={isErrorSaving && !formData.invcodigo}
                  inputProps={{ maxLength: 2 }}
                  onFocus={(e) => e.target.select()}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Descripción de Inventario *"
                  value={formData.invdescri}
                  onChange={(e) => handleInputChange("invdescri", e.target.value)}
                  error={isErrorSaving && !formData.invdescri}
                  inputProps={{ maxLength: 30 }}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid item xs={12} sm={3}>
                <TextField
                  select
                  fullWidth
                  label="Estado"
                  value={formData.invstatus}
                  onChange={(e) => setFormData((prev) => ({ ...prev, invstatus: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                >
                  <MenuItem value="A">ACTIVO</MenuItem>
                  <MenuItem value="I">INACTIVO</MenuItem>
                </TextField>
              </Grid>
            </Grid>
          </Paper>
        </Box>
      </div>
    </ThemeProvider>
  )
}

export default EditarInventariosINV
